import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardBody, CardHeader, Col, DropdownItem, DropdownMenu, DropdownToggle, UncontrolledDropdown, Spinner } from 'reactstrap';
import axios from 'axios';
import { useAuth } from '../../../Provider/AuthProvider';

interface Product {
    id: string;
    label: string;
    ref: string;
    date: string;
    price: number;
    orders: number;
    stock: number;
    amount: number;
    qty: number;
}

type SortColumn = 'qty' | 'orders' | 'amount';
type SortDirection = 'asc' | 'desc';

const BestSellingProducts = () => {
    const { user } = useAuth();
    const [products, setProducts] = useState<Product[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedPeriod, setSelectedPeriod] = useState("Tous");
    const [sortColumn, setSortColumn] = useState<SortColumn | null>(null);
    const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

    // Récupération des informations utilisateur
    const userinfo = user?.userinfo;
    const isAdmin = userinfo?.admin === "1";
    const isEmployee = userinfo?.admin === "0" && userinfo?.employee === "1";

    // Fonction pour tronquer le texte
    const truncateText = (text: string, maxLength: number) => {
        if (!text) return '';
        return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
    };

    // Fonction pour calculer les dates en fonction de la période sélectionnée
    const getDateRange = (period: string) => {
        const today = new Date();
        let date_start = null;
        let date_end = null;

        switch (period) {
            case "Aujourd'hui":
                date_start = formatDate(today);
                date_end = formatDate(today);
                break;
            case "Hier":
                const yesterday = new Date(today);
                yesterday.setDate(yesterday.getDate() - 1);
                date_start = formatDate(yesterday);
                date_end = formatDate(yesterday);
                break;
            case "7 derniers jours":
                date_end = formatDate(today);
                const last7Days = new Date(today);
                last7Days.setDate(last7Days.getDate() - 7);
                date_start = formatDate(last7Days);
                break;
            case "30 derniers jours":
                date_end = formatDate(today);
                const last30Days = new Date(today);
                last30Days.setDate(last30Days.getDate() - 30);
                date_start = formatDate(last30Days);
                break;
            case "Ce mois":
                date_start = formatDate(new Date(today.getFullYear(), today.getMonth(), 1));
                date_end = formatDate(today);
                break;
            case "Mois dernier":
                const firstDayLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                const lastDayLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
                date_start = formatDate(firstDayLastMonth);
                date_end = formatDate(lastDayLastMonth);
                break;
            case "Tous":
            default:
                date_start = null;
                date_end = null;
                break;
        }

        return { date_start, date_end };
    };

    // Fonction pour formater la date en YYYY-MM-DD
    const formatDate = (date: Date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const fetchTopSellingProducts = async (period = "Tous") => {
        setLoading(true);
        try {
            // Vérification de l'authentification
            if (!user?.token) {
                console.error('Utilisateur non authentifié - token manquant');
                setLoading(false);
                return;
            }

            if (!user?.entity) {
                console.error('Entité utilisateur manquante');
                setLoading(false);
                return;
            }

            const { date_start, date_end } = getDateRange(period);
            
            // Construire le payload
            const payload: any = {
                action: 'topselproduct',
                entity: user.entity
            };

            // Ajouter les dates seulement si elles sont définies
            if (date_start) {
                payload.date_start = date_start;
            }
            if (date_end) {
                payload.date_end = date_end;
            }

            console.log('Envoi de la requête avec:', {
                url: 'https://api.apapiecesauto.fr/apiV2/',
                payload,
                token: user.token ? 'Présent' : 'Manquant'
            });

            const response: any = await axios.post(
                'https://api.apapiecesauto.fr/apiV2/',
                payload,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${user.token}`,
                    }
                }
            );

            // Vérifier que la réponse est valide
            if (response.data && response.success === true) {
                // Transformer les données de l'API pour correspondre à l'interface Product
                const formattedProducts = response.data.map((item: any) => ({
                    id: item.id.toString(),
                    label: item.label,
                    ref: item.ref,
                    date: item.date || '',
                    price: item.price || 0,
                    orders: item.orders || 0,
                    stock: item.stock || 0,
                    amount: item.amount || 0,
                    qty: item.qty || 0
                }));
                
                setProducts(formattedProducts);
                console.log('Produits récupérés:', formattedProducts);
            } else {
                console.warn('La réponse API ne contient pas success=true', response.data);
                setProducts([]);
            }
        } catch (error: any) {
            console.error('Erreur lors de la récupération des produits:', error);
            
            // Gestion détaillée des erreurs
            if (error.response) {
                console.error('Détails de l\'erreur:', {
                    status: error.response.status,
                    data: error.response.data,
                    headers: error.response.headers
                });

                if (error.response.status === 401) {
                    console.error('Token invalide ou expiré - veuillez vous reconnecter');
                }
            } else if (error.request) {
                console.error('Aucune réponse reçue du serveur:', error.request);
            } else {
                console.error('Erreur de configuration:', error.message);
            }
            
            setProducts([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user?.token) {
            fetchTopSellingProducts(selectedPeriod);
        } else {
            console.log('En attente de l\'authentification...');
            setLoading(false);
        }
    }, [user?.token, selectedPeriod]);

    const handlePeriodChange = (period: string) => {
        setSelectedPeriod(period);
    };

    const handleSort = (column: SortColumn) => {
        if (sortColumn === column) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortColumn(column);
            setSortDirection('desc');
        }
    };

    const sortedProducts = React.useMemo(() => {
        if (!sortColumn) return products;
        const sorted = [...products].sort((a, b) => {
            const diff = a[sortColumn] - b[sortColumn];
            return sortDirection === 'asc' ? diff : -diff;
        });
        return sorted;
    }, [products, sortColumn, sortDirection]);

    const renderSortIcon = (column: SortColumn) => {
        if (sortColumn !== column) {
            return <i className="mdi mdi-unfold-more-horizontal ms-1 text-muted"></i>;
        }
        return sortDirection === 'asc'
            ? <i className="mdi mdi-arrow-up ms-1"></i>
            : <i className="mdi mdi-arrow-down ms-1"></i>;
    };

    const formatPrice = (price: number) => {
        return price?.toFixed(2) || '0.00';
    };

    const renderStockCell = (stock: number) => {
        let badgeClass = 'badge-soft-success';
        let label = 'En stock';
        let icon = 'ri-checkbox-circle-line';
        
        if (stock === 0) {
            badgeClass = 'badge-soft-danger';
            label = 'Rupture';
            icon = 'ri-close-circle-line';
        } else if (stock < 5) {
            badgeClass = 'badge-soft-warning';
            label = 'Stock faible';
            icon = 'ri-alert-line';
        } else if (stock < 10) {
            badgeClass = 'badge-soft-info';
            label = 'Stock moyen';
            icon = 'ri-information-line';
        }
        
        return (
            <div className="d-flex align-items-center gap-2">
                <div className={`badge ${badgeClass} p-2`}>
                    <i className={`${icon} me-1`}></i>
                    {stock}
                </div>
                <span className={`badge ${badgeClass}`}>{label}</span>
            </div>
        );
    };

    // Fonction pour obtenir l'icône en fonction du type de produit
    const getProductIcon = (product: Product) => {
        const ref = product.ref?.toLowerCase() || '';
        const label = product.label?.toLowerCase() || '';
        
        if (ref.includes('filtre') || label.includes('filtre')) {
            return 'ri-filter-line';
        } else if (ref.includes('huile') || label.includes('huile')) {
            return 'ri-oil-line';
        } else if (ref.includes('roulement') || label.includes('roulement')) {
            return 'ri-settings-line';
        } else if (ref.includes('plaquette') || label.includes('plaquette')) {
            return 'ri-disc-line';
        } else {
            return 'ri-shopping-bag-line';
        }
    };

    // Styles inline pour le tableau avec hauteur fixe
    const tableContainerStyle = {
        maxHeight: '400px',
        overflowY: 'auto' as const,
        scrollbarWidth: 'thin' as const,
        msOverflowStyle: 'auto' as const
    };

    const tableHeadStyle = {
        position: 'sticky' as const,
        top: 0,
        zIndex: 1,
        backgroundColor: '#f8f9fa'
    };

    // Vérification si l'utilisateur est authentifié
    if (!user?.token) {
        return (
            <Col xl={6}>
                <Card>
                    <CardBody>
                        <div className="text-center py-4">
                            <i className="ri-error-warning-line display-5 text-warning"></i>
                            <h5 className="mt-3">Authentification requise</h5>
                            <p className="text-muted">
                                Veuillez vous connecter pour voir les meilleures ventes
                            </p>
                        </div>
                    </CardBody>
                </Card>
            </Col>
        );
    }

    return (
        <Col xl={6}>
            <Card>
                <CardHeader className="align-items-center d-flex">
                    <h4 className="card-title mb-0 flex-grow-1">Top Ventes</h4>
                    <div className="flex-shrink-0">
                        <UncontrolledDropdown className="card-header-dropdown">
                            <DropdownToggle tag="a" className="text-reset" role="button">
                                <span className="fw-semibold text-uppercase fs-12">Période: </span>
                                <span className="text-muted">
                                    {selectedPeriod} <i className="mdi mdi-chevron-down ms-1"></i>
                                </span>
                            </DropdownToggle>
                            <DropdownMenu className="dropdown-menu-end">
                                <DropdownItem onClick={() => handlePeriodChange("Tous")}>
                                    Tous
                                </DropdownItem>
                                <DropdownItem onClick={() => handlePeriodChange("Aujourd'hui")}>
                                    Aujourd'hui
                                </DropdownItem>
                                <DropdownItem onClick={() => handlePeriodChange("Hier")}>
                                    Hier
                                </DropdownItem>
                                <DropdownItem onClick={() => handlePeriodChange("7 derniers jours")}>
                                    7 derniers jours
                                </DropdownItem>
                                <DropdownItem onClick={() => handlePeriodChange("30 derniers jours")}>
                                    30 derniers jours
                                </DropdownItem>
                                <DropdownItem onClick={() => handlePeriodChange("Ce mois")}>
                                    Ce mois
                                </DropdownItem>
                                <DropdownItem onClick={() => handlePeriodChange("Mois dernier")}>
                                    Mois dernier
                                </DropdownItem>
                            </DropdownMenu>
                        </UncontrolledDropdown>
                    </div>
                </CardHeader>

                <CardBody>
                    {loading && (
                        <div className="text-center py-4">
                            <Spinner color="primary">
                                Chargement...
                            </Spinner>
                        </div>
                    )}
                    
                    {!loading && products.length > 0 && (
                        <div className="table-responsive" style={tableContainerStyle}>
                            <table className="table table-hover table-centered align-middle table-nowrap mb-0">
                                <thead className="text-muted table-light" style={tableHeadStyle}>
                                    <tr>
                                        <th scope="col">Produit</th>
                                        <th scope="col" className="text-end">Prix</th>
                                        <th
                                            scope="col"
                                            className="text-center"
                                            role="button"
                                            onClick={() => handleSort('qty')}
                                        >
                                            Qté {renderSortIcon('qty')}
                                        </th>
                                        <th
                                            scope="col"
                                            className="text-center"
                                            role="button"
                                            onClick={() => handleSort('orders')}
                                        >
                                            Commandes {renderSortIcon('orders')}
                                        </th>
                                        {/* Affichage conditionnel de la colonne Montant */}
                                        {isAdmin && (
                                            <th
                                                scope="col"
                                                className="text-end"
                                                role="button"
                                                onClick={() => handleSort('amount')}
                                            >
                                                Montant {renderSortIcon('amount')}
                                            </th>
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedProducts.map((item, key) => (
                                        <tr key={item.id || key}>
                                            <td>
                                                <div className="d-flex align-items-center">
                                                    <div className="avatar-sm bg-light rounded p-1 me-2">
                                                        <div className="avatar-title bg-primary-subtle text-primary rounded fs-3">
                                                            <i className={getProductIcon(item)}></i>
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <h5 className="fs-14 my-1">
                                                            <Link to={`/apps-ecommerce-product-details/${item.id}`} className="text-reset" title={item.label}>
                                                                {truncateText(item.label, 25)}
                                                            </Link>
                                                        </h5>
                                                        <span className="text-muted">
                                                            Réf: {item.ref} | {item.date}
                                                        </span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="text-end">
                                                <h5 className="fs-14 my-1 fw-normal">
                                                    {item.price > 0 ? `${formatPrice(item.price)} €` : 'Prix non défini'}
                                                </h5>
                                                <span className="text-muted">Prix unitaire</span>
                                            </td>
                                            <td className="text-center">
                                                <h5 className="fs-14 my-1 fw-normal">{item.qty}</h5>
                                                <span className="text-muted">Qté vendue</span>
                                            </td>
                                            <td className="text-center">
                                                <h5 className="fs-14 my-1 fw-normal">{item.orders}</h5>
                                                <span className="text-muted">Commandes</span>
                                            </td>
                                            {isAdmin && (
                                                <td className="text-end">
                                                    <h5 className="fs-14 my-1 fw-normal">{formatPrice(item.amount)} €</h5>
                                                    <span className="text-muted">Montant total</span>
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    
                    {!loading && products.length === 0 && (
                        <div className="text-center py-4">
                            <i className="ri-information-line display-5 text-muted"></i>
                            <p className="text-muted mb-0 mt-3">Aucun produit trouvé pour la période sélectionnée</p>
                        </div>
                    )}
                    
                    {products.length > 0 && (
                        <div className="mt-3 text-center">
                            <small className="text-muted">
                                Affichage des {products.length} meilleures ventes
                                {selectedPeriod !== "Tous" && ` pour la période: ${selectedPeriod}`}
                            </small>
                        </div>
                    )}
                </CardBody>
            </Card>
        </Col>
    );
};

export default BestSellingProducts;